import cn from "classnames";
import { Children, cloneElement } from "react";

export interface SelectOption<V extends string = string> {
  label: string;
  value: V;
  disabled?: boolean;
  key?: string;
  readOnly?: boolean;
}

export interface SelectProps<V extends string = string> {
  id?: string;
  options: SelectOption<V>[];
  defaultValue?: string;
  value?: string;
  onSelect: (s: SelectOption<V>) => void;
  className?: string;
  ariaLabel?: string;
  disabled?: boolean;
  placeholder?: string;
}

export function Select<V extends string = string>({
  id,
  value,
  options,
  onSelect,
  defaultValue,
  className = "",
  ariaLabel = "combobox",
  disabled = false,
  placeholder = "Select",
}: SelectProps<V>) {
  const finClassName = cn(
    "border-gray-300 text-black",
    "hover:border-gray-500",
    "focus:ring-gray-500 focus:border-gray-500",
    "active:text-black",
    "disabled:bg-black-50 disabled:border-black-100 disabled:text-black",
    "rounded-md shadow-sm",
    className,
  );
  return (
    <select
      id={id}
      aria-label={ariaLabel}
      className={finClassName}
      value={value}
      defaultValue={defaultValue}
      aria-placeholder={placeholder}
      onChange={(e) => {
        const value = e.currentTarget.value;
        const option = options.find((o) => o.value === value);
        if (!option) return;
        onSelect(option as any);
      }}
      disabled={disabled}
    >
      {options.map((option) => {
        return (
          <option
            key={option.key || option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        );
      })}
    </select>
  );
}

type InputValue = string | number | readonly string[] | undefined;

interface RadioProps<V extends InputValue> {
  name: string;
  selected: V;
  onSelect: (v: V) => void;
}

export function Radio<V extends InputValue = string>({
  value,
  children,
  selected,
  name = "",
  onSelect = () => {},
  disabled = false,
  className = "",
}: {
  value: V;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
} & Partial<RadioProps<V>>) {
  return (
    <label className="flex items-center">
      <input
        type="radio"
        name={name}
        value={value}
        checked={selected === value}
        onChange={() => onSelect(value)}
        disabled={disabled}
      />
      <span className={`ml-1 ${className}`}>{children}</span>
    </label>
  );
}

export function RadioGroup<V extends InputValue>({
  children,
  className = "",
  orientation = "vertical",
  ...props
}: {
  children: React.ReactNode;
  className?: string;
  orientation?: "horizontal" | "vertical";
} & RadioProps<V>) {
  return (
    <div
      className={cn(
        "flex gap-2",
        {
          "flex-col": orientation === "vertical",
          "flex-row items-center": orientation === "horizontal",
        },
        className,
      )}
    >
      {Children.map(children, (child) =>
        cloneElement(child as any, { ...props }),
      )}
    </div>
  );
}
